/**
 * API-related type definitions for the frontend HTTP client
 */

/**
 * HTTP methods supported by the API client
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

/**
 * Configuration options for API requests
 */
export interface RequestConfig {
  /** Request headers to include */
  headers?: Record<string, string>;
  /** Query parameters to append to the URL */
  params?: Record<string, string | number | boolean | undefined>;
  /** Request body (automatically serialized to JSON) */
  body?: unknown;
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Whether to include credentials (cookies) */
  credentials?: RequestCredentials;
  /** AbortSignal for request cancellation */
  signal?: AbortSignal;
}

/**
 * API response wrapper with typed data
 */
export interface ApiResponse<T> {
  /** The response data */
  data: T;
  /** HTTP status code */
  status: number;
  /** HTTP status text */
  statusText: string;
  /** Response headers */
  headers: Headers;
}

/**
 * Error response structure from the API
 */
export interface ApiErrorResponse {
  /** Error message */
  message: string;
  /** Error code for programmatic handling */
  code?: string;
  /** Additional error details */
  details?: Record<string, unknown>;
  /** Validation errors keyed by field name */
  errors?: Record<string, string[]>;
}

/**
 * Custom API error class with enhanced error information
 */
export class ApiError extends Error {
  /** HTTP status code */
  readonly status: number;
  /** HTTP status text */
  readonly statusText: string;
  /** Error code for programmatic handling */
  readonly code: string | undefined;
  /** Additional error details */
  readonly details: Record<string, unknown> | undefined;
  /** Validation errors keyed by field name */
  readonly errors: Record<string, string[]> | undefined;
  /** Original response data */
  readonly response: ApiErrorResponse | undefined;

  constructor(
    message: string,
    status: number,
    statusText: string,
    response?: ApiErrorResponse
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.response = response;
    this.code = response?.code;
    this.details = response?.details;
    this.errors = response?.errors;

    // Maintains proper stack trace for where error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  /**
   * Check if this is a specific HTTP error
   */
  isStatus(status: number): boolean {
    return this.status === status;
  }

  /**
   * Check if this is an authentication error (401)
   */
  isUnauthorized(): boolean {
    return this.status === 401;
  }

  /**
   * Check if this is a forbidden error (403)
   */
  isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * Check if this is a not found error (404)
   */
  isNotFound(): boolean {
    return this.status === 404;
  }

  /**
   * Check if this is a validation error (422)
   */
  isValidationError(): boolean {
    return this.status === 422;
  }

  /**
   * Check if this is a server error (5xx)
   */
  isServerError(): boolean {
    return this.status >= 500 && this.status < 600;
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage(): string {
    // Return custom message if provided
    if (this.response?.message) {
      return this.response.message;
    }

    // Default user-friendly messages based on status
    switch (this.status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Your session has expired. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'A conflict occurred. The resource may have been modified.';
      case 422:
        return 'Please correct the validation errors and try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'An unexpected server error occurred. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'The service is temporarily unavailable. Please try again later.';
      default:
        return this.message || 'An unexpected error occurred.';
    }
  }
}

/**
 * Request interceptor function type
 */
export type RequestInterceptor = (
  url: string,
  config: RequestInit
) => Promise<{ url: string; config: RequestInit }> | { url: string; config: RequestInit };

/**
 * Response interceptor function type
 */
export type ResponseInterceptor = (
  response: Response,
  request: { url: string; config: RequestInit }
) => Promise<Response> | Response;

/**
 * Error interceptor function type
 */
export type ErrorInterceptor = (
  error: ApiError,
  request: { url: string; config: RequestInit }
) => Promise<ApiError | never> | ApiError | never;

/**
 * Interceptors collection
 */
export interface Interceptors {
  request: RequestInterceptor[];
  response: ResponseInterceptor[];
  error: ErrorInterceptor[];
}

/**
 * API client configuration options
 */
export interface ApiClientConfig {
  /** Base URL for all API requests */
  baseUrl: string;
  /** Default request timeout in milliseconds */
  timeout?: number;
  /** Default headers to include in all requests */
  defaultHeaders?: Record<string, string>;
  /** Function to get the current auth token */
  getAuthToken?: () => string | null | Promise<string | null>;
  /** Handler for 401 unauthorized responses */
  onUnauthorized?: () => void | Promise<void>;
}
